import { _, helpers, mui, React, ts, ui } from '_core';

import { filterTabBarDataByReturnHorizon, prepareData } from './helpers';
import { Params } from './types';
import { nestedBarChartFormat } from '../../common/formatters/nested-bar-chart';
import { getLabel, prepareRhDataBarChart } from '../../common/utils/signal-utils';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  returns: string;
  fullScreen: boolean;
  params: Params;
  frequency: ts.enums.FREQUENCY_ENUM;
  title: string;
  subtitle?: string;
  setExternalEmbed: (_json: ts.types.analysis.EmbedChartData) => void;
};

const Chart = ({
  id,
  readOnly,
  loadWidgetData,
  returns,
  fullScreen,
  params,
  frequency,
  title,
  subtitle,
  setExternalEmbed,
}: ChartProps) => {
  const hasBenchmark = !_.isEmpty(params.benchmark);

  const [tab, setTab] = React.useState(params.return_horizons[0].signature);
  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const loadData = async () => {
    try {
      const response = await loadWidgetData({
        data: {
          file: returns,
        },
      });

      if (response) {
        setData(prepareData(response.data, params));
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    if (returns) loadData();
    return () => setData(null);
  }, [returns]);

  const getTabs = () => {
    if (data && hasBenchmark) {
      return (
        <mui.core.Tabs
          value={tab}
          indicatorColor="primary"
          textColor="primary"
          onChange={(e, newTab) => setTab(newTab)}
          aria-label="Return Horizon Tabs"
        >
          {params.return_horizons.map((rh) => (
            <mui.core.Tab key={rh.signature} label={getLabel(rh.signature, frequency)} value={rh.signature} />
          ))}
        </mui.core.Tabs>
      );
    }
    return null;
  };

  const formattedData = React.useMemo(() => {
    if (_.isNil(data)) return null;
    const localData = hasBenchmark ? filterTabBarDataByReturnHorizon(data, tab) : data;
    return prepareRhDataBarChart(nestedBarChartFormat(localData), frequency);
  }, [data, hasBenchmark, tab, frequency]);

  return (
    <mui.core.Box display="flex" flexDirection="column" height="100%">
      {getTabs()}
      <mui.core.Box flexGrow={1}>
        {!_.isNil(formattedData) && (
          <ui.NestedBarChart
            id={id}
            data={formattedData?.data}
            count={formattedData?.count}
            category="category"
            alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
            fullScreen={fullScreen}
            numberFormat={params.statistics_type !== 'adjusted' ? '#.##%' : '#.##'}
            exportTitle={title}
            exportSubTitle={subtitle}
            setExternalEmbed={setExternalEmbed}
            readOnly={readOnly}
          />
        )}
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
